import { supabase } from '../lib/supabase';
import { callEdgeFunction } from '../pages/admin/callEdgeFunction';

/** Read a browser File as raw base64 (for admin Edge Function uploads). */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('فشل قراءة الملف'));
        return;
      }
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(new Error('فشل قراءة الملف'));
    reader.readAsDataURL(file);
  });
}

async function adminWizardUpload(operation, params) {
  return await callEdgeFunction('admin-db-action', { operation, params });
}

const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// للقراءة: يستخدم جلسة المشرف الحالية عبر supabase العادي
export const getAdminClient = () => supabase;

// ─── Booking status constants (must match actual DB values) ─────────────────
const BOOKING_ACTIVE   = ['pending_photographer_confirmation', 'awaiting_payment', 'paid_confirmed'];
const BOOKING_DONE     = 'completed';
const BOOKING_CANCELLED = ['cancelled_by_customer', 'cancelled_by_admin', 'expired'];

export const adminService = {
  // ─── Helper: compute ISO start date from dateFilter key ───────────────────
  _getStartDate(dateFilter) {
    const now = new Date();
    switch (dateFilter) {
      case 'this_month':
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      case 'last_month':
        return new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
      case 'last_3_months':
        return new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString();
      default: // last_6_months
        return new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString();
    }
  },

  // ─── Helper: how many months to show in trend charts ────────────────────
  _getMonthCount(dateFilter) {
    if (dateFilter === 'this_month' || dateFilter === 'last_month') return 1;
    if (dateFilter === 'last_3_months') return 3;
    return 6;
  },

  // Get platform-wide statistics
  async getPlatformStats(dateFilter = 'last_6_months') {
    const client = getAdminClient();
    const startDate = this._getStartDate(dateFilter);

    const [
      { count: totalBookings },
      { data: completedBookings },
      { count: activePhotographers },
      { count: totalCustomers },
    ] = await Promise.all([
      client.from('bookings').select('*', { count: 'exact', head: true }),
      client.from('bookings').select('price').eq('status', BOOKING_DONE).gte('created_at', startDate),
      client.from('user_profiles').select('*', { count: 'exact', head: true }).eq('role', 'photographer'),
      client.from('user_profiles').select('*', { count: 'exact', head: true }).eq('role', 'customer'),
    ]);

    const totalRevenue = completedBookings?.reduce((sum, b) => sum + parseFloat(b?.price || 0), 0) || 0;

    return {
      totalBookings: totalBookings || 0,
      totalRevenue,
      activePhotographers: activePhotographers || 0,
      totalCustomers: totalCustomers || 0,
    };
  },

  // Get booking trends grouped by month
  async getBookingTrends(dateFilter = 'last_6_months') {
    const client = getAdminClient();
    const startDate = this._getStartDate(dateFilter);
    const monthCount = this._getMonthCount(dateFilter);

    const { data, error } = await client
      .from('bookings')
      .select('created_at, status')
      .gte('created_at', startDate)
      .order('created_at', { ascending: true });

    if (error) throw error;

    const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
    const monthlyData = {};

    data?.forEach(b => {
      const date = new Date(b?.created_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[key]) monthlyData[key] = { total: 0, completed: 0, cancelled: 0, active: 0 };
      monthlyData[key].total++;
      if (b?.status === BOOKING_DONE) monthlyData[key].completed++;
      else if (BOOKING_CANCELLED.includes(b?.status)) monthlyData[key].cancelled++;
      else if (BOOKING_ACTIVE.includes(b?.status)) monthlyData[key].active++;
    });

    const now = new Date();
    const result = [];
    for (let i = monthCount - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      result.push({
        month: months[d.getMonth()],
        total: monthlyData[key]?.total || 0,
        completed: monthlyData[key]?.completed || 0,
        cancelled: monthlyData[key]?.cancelled || 0,
        pending: monthlyData[key]?.active || 0,
      });
    }
    return result;
  },

  // Get revenue trends grouped by month (from completed bookings)
  async getRevenueTrends(dateFilter = 'last_6_months') {
    const client = getAdminClient();
    const startDate = this._getStartDate(dateFilter);
    const monthCount = this._getMonthCount(dateFilter);

    const { data, error } = await client
      .from('bookings')
      .select('created_at, price')
      .eq('status', BOOKING_DONE)
      .gte('created_at', startDate)
      .order('created_at', { ascending: true });

    if (error) throw error;

    const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
    const monthlyData = {};

    data?.forEach(b => {
      const date = new Date(b?.created_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyData[key] = (monthlyData[key] || 0) + parseFloat(b?.price || 0);
    });

    const now = new Date();
    const result = [];
    for (let i = monthCount - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      result.push({ month: months[d.getMonth()], revenue: monthlyData[key] || 0 });
    }
    return result;
  },

  // Get revenue breakdown by photography type
  async getRevenueByType(dateFilter = 'last_6_months') {
    const client = getAdminClient();
    const startDate = this._getStartDate(dateFilter);

    const { data: bookings } = await client
      .from('bookings')
      .select('photography_type, price')
      .eq('status', BOOKING_DONE)
      .gte('created_at', startDate);

    const typeData = {};
    bookings?.forEach(b => {
      const type = b?.photography_type || 'غير محدد';
      typeData[type] = (typeData[type] || 0) + parseFloat(b?.price || 0);
    });

    return Object.entries(typeData)
      .map(([type, revenue]) => ({ type, revenue }))
      .sort((a, b) => b.revenue - a.revenue);
  },

  // Get revenue breakdown by city
  async getRevenueByCity(dateFilter = 'last_6_months') {
    const client = getAdminClient();
    const startDate = this._getStartDate(dateFilter);

    const { data: bookings } = await client
      .from('bookings')
      .select('city, price')
      .eq('status', BOOKING_DONE)
      .gte('created_at', startDate);

    const cityData = {};
    bookings?.forEach(b => {
      const city = b?.city || 'غير محدد';
      cityData[city] = (cityData[city] || 0) + parseFloat(b?.price || 0);
    });

    return Object.entries(cityData)
      .map(([city, revenue]) => ({ city, revenue }))
      .sort((a, b) => b.revenue - a.revenue);
  },

  // Get photographer performance rankings — single batch fetch, no N+1
  async getPhotographerPerformance() {
    const client = getAdminClient();

    const [
      { data: photographers },
      { data: allBookings },
      { data: allReviews },
    ] = await Promise.all([
      client.from('user_profiles').select('id, full_name, avatar_url').eq('role', 'photographer'),
      client.from('bookings').select('photographer_id, status, price'),
      client.from('reviews').select('photographer_id, rating'),
    ]);

    const bookingsByPhotographer = {};
    allBookings?.forEach(b => {
      const pid = b?.photographer_id;
      if (!pid) return;
      if (!bookingsByPhotographer[pid]) bookingsByPhotographer[pid] = [];
      bookingsByPhotographer[pid].push(b);
    });

    const reviewsByPhotographer = {};
    allReviews?.forEach(r => {
      const pid = r?.photographer_id;
      if (!pid) return;
      if (!reviewsByPhotographer[pid]) reviewsByPhotographer[pid] = [];
      reviewsByPhotographer[pid].push(r);
    });

    const performanceData = (photographers || []).map(photographer => {
      const pBookings = bookingsByPhotographer[photographer?.id] || [];
      const pReviews = reviewsByPhotographer[photographer?.id] || [];
      const completed = pBookings.filter(b => b?.status === BOOKING_DONE);
      const earnings = completed.reduce((sum, b) => sum + parseFloat(b?.price || 0), 0);
      const avgRating = pReviews.length > 0
        ? pReviews.reduce((sum, r) => sum + r?.rating, 0) / pReviews.length
        : 0;

      return {
        id: photographer?.id,
        name: photographer?.full_name,
        avatar: photographer?.avatar_url,
        totalBookings: pBookings.length,
        completedBookings: completed.length,
        completionRate: pBookings.length > 0 ? Math.round((completed.length / pBookings.length) * 100) : 0,
        earnings,
        avgRating: avgRating.toFixed(1),
        reviewCount: pReviews.length,
      };
    });

    return performanceData.sort((a, b) => b.earnings - a.earnings);
  },

  // Get customer insights
  async getCustomerInsights() {
    const client = getAdminClient();

    const [
      { data: customers },
      { data: bookingCounts },
      { data: allReviews },
    ] = await Promise.all([
      client.from('user_profiles').select('id, created_at').eq('role', 'customer'),
      client.from('bookings').select('customer_id'),
      client.from('reviews').select('rating'),
    ]);

    const currentMonth = new Date().toISOString().slice(0, 7);
    const newCustomers = customers?.filter(c => c?.created_at?.startsWith(currentMonth))?.length || 0;

    const customerBookingMap = {};
    bookingCounts?.forEach(b => {
      customerBookingMap[b?.customer_id] = (customerBookingMap[b?.customer_id] || 0) + 1;
    });
    const repeatCustomers = Object.values(customerBookingMap).filter(count => count > 1).length;

    const totalCust = customers?.length || 0;
    const avgBookingsPerCustomer = totalCust > 0
      ? ((bookingCounts?.length || 0) / totalCust).toFixed(1)
      : 0;

    const avgSatisfaction = allReviews?.length > 0
      ? (allReviews.reduce((sum, r) => sum + r?.rating, 0) / allReviews.length).toFixed(1)
      : 0;

    return {
      totalCustomers: totalCust,
      newCustomers,
      repeatCustomers,
      retentionRate: totalCust > 0 ? Math.round((repeatCustomers / totalCust) * 100) : 0,
      avgBookingsPerCustomer,
      avgSatisfaction,
    };
  },

  // Get current platform activity
  async getCurrentActivity() {
    const client = getAdminClient();

    const [
      { count: activeBookings },
      { count: awaitingPayment },
      { count: availablePhotographers },
    ] = await Promise.all([
      client.from('bookings').select('*', { count: 'exact', head: true }).in('status', BOOKING_ACTIVE),
      client.from('bookings').select('*', { count: 'exact', head: true }).eq('status', 'awaiting_payment'),
      client.from('user_profiles').select('*', { count: 'exact', head: true }).eq('role', 'photographer'),
    ]);

    return {
      activeBookings: activeBookings || 0,
      pendingPayments: awaitingPayment || 0,
      availablePhotographers: availablePhotographers || 0,
    };
  },

  // Get dashboard metrics for admin master dashboard (يستخدم عميل المشرف إن وُجد لتجاوز RLS)
  async getDashboardMetrics() {
    const client = getAdminClient();
    try {
      const { count: totalUsers } = await client?.from('user_profiles')?.select('*', { count: 'exact', head: true });
      const { count: activeBookings } = await client?.from('bookings')?.select('*', { count: 'exact', head: true })?.in('status', ['pending', 'confirmed']);
      const { data: payments } = await client?.from('payments')?.select('amount, status');
      const totalRevenue = payments?.filter(p => p?.status === 'completed')?.reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0);
      return {
        totalUsers: totalUsers || 0,
        activeBookings: activeBookings || 0,
        totalRevenue: totalRevenue || 0
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  },

  // Get recent platform activities
  async getRecentActivities() {
    try {
      const activities = [];

      // Recent user registrations
      const { data: recentUsers } = await supabase?.from('user_profiles')?.select('full_name, created_at, role')?.order('created_at', { ascending: false })?.limit(5);
      recentUsers?.forEach(user => {
        activities?.push({
          id: `user-${user?.created_at}`,
          type: 'user_registered',
          title: `مستخدم جديد: ${user?.full_name}`,
          description: `انضم ${user?.role === 'photographer' ? 'مصور' : 'عميل'} جديد إلى المنصة`,
          timestamp: new Date(user?.created_at)?.toLocaleString('ar-SA')
        });
      });

      // Recent completed bookings
      const { data: recentBookings } = await supabase?.from('bookings')?.select('id, status, created_at')?.eq('status', 'completed')?.order('created_at', { ascending: false })?.limit(5);
      recentBookings?.forEach(booking => {
        activities?.push({
          id: `booking-${booking?.id}`,
          type: 'booking_completed',
          title: 'حجز مكتمل',
          description: `تم إكمال حجز بنجاح`,
          timestamp: new Date(booking?.created_at)?.toLocaleString('ar-SA')
        });
      });

      // Recent payments
      const { data: recentPayments } = await supabase?.from('payments')?.select('amount, created_at')?.eq('status', 'completed')?.order('created_at', { ascending: false })?.limit(5);
      recentPayments?.forEach(payment => {
        activities?.push({
          id: `payment-${payment?.created_at}`,
          type: 'payment_received',
          title: 'دفعة مستلمة',
          description: `تم استلام دفعة بقيمة ${payment?.amount} ريال`,
          timestamp: new Date(payment?.created_at)?.toLocaleString('ar-SA')
        });
      });

      // Sort by timestamp and return latest 10
      return activities?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp))?.slice(0, 10);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return [];
    }
  },

  // Get user statistics by role
  async getUserStats() {
    try {
      // Always use regular supabase client (RLS policies now allow public read)
      // This ensures consistency and works even without service role key
      console.log('🔍 getUserStats: Fetching user statistics...');
      
      // Method 1: Try count queries first (fastest)
      const { count: customers, error: customersError } = await supabase
        ?.from('user_profiles')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('role', 'customer');
      
      const { count: photographers, error: photographersError } = await supabase
        ?.from('user_profiles')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('role', 'photographer');
      
      const { count: admins, error: adminsError } = await supabase
        ?.from('user_profiles')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('role', 'admin');
      
      // If count queries work, return them
      if (!customersError && !photographersError && !adminsError) {
        console.log('✅ getUserStats: Count queries successful', { customers, photographers, admins });
        return {
          customers: customers || 0,
          photographers: photographers || 0,
          admins: admins || 0
        };
      }
      
      // Method 2: If count queries failed, get all users and count manually
      console.log('⚠️ getUserStats: Count queries failed, trying manual count...');
      console.log('Errors:', { customersError, photographersError, adminsError });
      
      const { data: allUsers, error: allUsersError } = await supabase
        ?.from('user_profiles')
        ?.select('role');
      
      if (allUsersError) {
        console.error('❌ getUserStats: Failed to fetch users:', allUsersError);
        throw allUsersError;
      }
      
      if (!allUsers || allUsers.length === 0) {
        console.warn('⚠️ getUserStats: No users found');
        return {
          customers: 0,
          photographers: 0,
          admins: 0
        };
      }
      
      // Count manually
      const manualCustomers = allUsers.filter(u => u.role === 'customer').length;
      const manualPhotographers = allUsers.filter(u => u.role === 'photographer').length;
      const manualAdmins = allUsers.filter(u => u.role === 'admin').length;
      
      console.log('✅ getUserStats: Manual count successful', { 
        manualCustomers, 
        manualPhotographers, 
        manualAdmins,
        totalUsers: allUsers.length
      });
      
      return {
        customers: manualCustomers || 0,
        photographers: manualPhotographers || 0,
        admins: manualAdmins || 0
      };
    } catch (error) {
      console.error('❌ getUserStats: Exception:', error);
      // Return default values instead of throwing
      return {
        customers: 0,
        photographers: 0,
        admins: 0
      };
    }
  }
  ,

  // ===== Admin Mutations — جميعها تمر عبر Edge Function آمنة =====

  async setUserActiveStatus(userId, isActive) {
    return await callEdgeFunction('admin-db-action', {
      operation: 'set_user_active',
      params: { userId, isActive },
    });
  },

  async setPhotographerAvailability(userId, availableForBooking) {
    return await callEdgeFunction('admin-db-action', {
      operation: 'set_photographer_available',
      params: { userId, available: availableForBooking },
    });
  },

  async setBookingStatus(bookingId, status) {
    return await callEdgeFunction('admin-db-action', {
      operation: 'set_booking_status',
      params: { bookingId, status },
    });
  },

  async detectPricingTable() {
    const r1 = await supabase.from('pricing_rules').select('id').limit(1);
    if (!r1?.error) return 'pricing_rules';
    const r2 = await supabase.from('pricing_settings').select('id').limit(1);
    if (!r2?.error) return 'pricing_settings';
    return null;
  },

  async upsertPricingRule(table, payload) {
    if (!table) throw new Error('لا يوجد جدول تسعير متاح.');
    return await callEdgeFunction('admin-db-action', {
      operation: 'upsert_pricing',
      params: { table, payload },
    });
  },

  async deletePricingRule(table, id) {
    if (!table) throw new Error('لا يوجد جدول تسعير متاح.');
    return await callEdgeFunction('admin-db-action', {
      operation: 'delete_pricing',
      params: { table, id },
    });
  },

  async createPhotographer(photographerData) {
    const tempPassword = Math.random().toString(36).slice(-12) + 'A1!';
    const result = await callEdgeFunction('admin-db-action', {
      operation: 'create_photographer',
      params: { ...photographerData, password: tempPassword },
    });
    return { user: { id: result.userId } };
  },

  async uploadPhotographerWizardAvatar({ userId, file }) {
    const fileBase64 = await fileToBase64(file);
    return await adminWizardUpload('upload_photographer_wizard_avatar', {
      userId,
      fileBase64,
      fileName: file.name,
      contentType: file.type,
    });
  },

  async uploadPhotographerWizardPortfolioItem({ userId, file, category }) {
    const fileBase64 = await fileToBase64(file);
    return await adminWizardUpload('upload_photographer_wizard_portfolio_item', {
      userId,
      fileBase64,
      fileName: file.name,
      contentType: file.type,
      title: file.name.replace(/\.[^/.]+$/, ''),
      category: category || 'عام',
    });
  },

  async uploadPhotographerWizardIdDocument({ userId, file, photographerName }) {
    const fileBase64 = await fileToBase64(file);
    return await adminWizardUpload('upload_photographer_wizard_id_document', {
      userId,
      fileBase64,
      fileName: file.name,
      contentType: file.type,
      photographerName,
    });
  },

  async uploadPhotographerWizardDronePermit({ userId, file, photographerName }) {
    const fileBase64 = await fileToBase64(file);
    return await adminWizardUpload('upload_photographer_wizard_drone_permit', {
      userId,
      fileBase64,
      fileName: file.name,
      contentType: file.type,
      photographerName,
    });
  },

  async uploadPhotographerWizardFreelancePermit({ userId, file, photographerName }) {
    const fileBase64 = await fileToBase64(file);
    return await adminWizardUpload('upload_photographer_wizard_freelance_permit', {
      userId,
      fileBase64,
      fileName: file.name,
      contentType: file.type,
      photographerName,
    });
  },

  async createCustomer(customerData) {
    const tempPassword = Math.random().toString(36).slice(-12) + 'A1!';
    const result = await callEdgeFunction('admin-db-action', {
      operation: 'create_customer',
      params: { ...customerData, password: tempPassword },
    });
    return { user: { id: result.userId }, tempPassword };
  },

  async deleteUser(userId) {
    await callEdgeFunction('admin-db-action', {
      operation: 'delete_user',
      params: { userId },
    });
    return true;
  },

  async managePhotographerDirect(photographerId, action, value) {
    return await callEdgeFunction('admin_manage_photographer', {
      photographerId,
      action,
      value,
    });
  }

};